<template>
  <div
    v-if="hasSlides"
    class="team-carousel-3d"
    role="region"
    aria-label="Členové týmu"
  >
    <div
      ref="carouselContainer"
      class="team-carousel-container"
      role="group"
      aria-roledescription="carousel"
      :aria-label="`Slide ${currentIndex + 1} ze ${slides.length}`"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div
        v-for="slide in visibleSlides"
        :key="`team-${slide.actualIndex}`"
        class="team-carousel-slide"
        role="group"
        aria-roledescription="slide"
        :aria-hidden="Math.abs(slide.offset) >= 0.5"
        :class="{
          'is-center': Math.abs(slide.offset) < 0.5,
          'is-active': Math.abs(slide.offset) < 0.5
        }"
        :style="getSlideStyle(slide.offset)"
      >
        <div class="team-carousel-person">
          <Transition
            name="image-fade"
            mode="out-in"
          >
            <img
              :key="`img-${slide.id}-${getCurrentImage(slide)}`"
              :src="getCurrentImage(slide)"
              :alt="slide.name"
              class="team-person-image"
            >
          </Transition>
        </div>
      </div>

      <!-- Fixed info box that stays in center -->
      <div
        v-if="centerSlide"
        class="team-carousel-info"
        aria-live="polite"
        aria-atomic="true"
      >
        <Transition
          name="text-fade"
          mode="out-in"
        >
          <div
            :key="`info-${currentIndex}-${showingAlternateContent.get(centerSlide.id)}`"
            class="team-carousel-info-content"
          >
            <h3>{{ centerSlide.name }}</h3>
            <Transition
              name="content-fade"
              mode="out-in"
            >
              <p
                :key="`text-${centerSlide.id}-${showingAlternateContent.get(centerSlide.id)}`"
                class="team-person-text"
              >
                {{ getCurrentText(centerSlide) }}
              </p>
            </Transition>
            <button
              class="team-info-button"
              @click="handleButtonClick(centerSlide)"
            >
              <span>Více informací</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                fill="currentColor"
              >
                <path d="M352 128C352 110.3 337.7 96 320 96C302.3 96 288 110.3 288 128L288 288L128 288C110.3 288 96 302.3 96 320C96 337.7 110.3 352 128 352L288 352L288 512C288 529.7 302.3 544 320 544C337.7 544 352 529.7 352 512L352 352L512 352C529.7 352 544 337.7 544 320C544 302.3 529.7 288 512 288L352 288L352 128z" />
              </svg>
            </button>
          </div>
        </Transition>
      </div>
    </div>

    <div class="team-carousel-controls">
      <button
        class="carousel-control-btn carousel-prev"
        :disabled="isTransitioning || !canNavigate"
        aria-label="Předchozí člen týmu"
        @click="prev"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 57 57"
        >
          <path
            fill="currentColor"
            d="M36.599 39.377 25.72 28.5 36.6 17.599l-3.35-3.349L19 28.5l14.25 14.25 3.349-3.373z"
          />
        </svg>
      </button>
      <button
        class="carousel-control-btn carousel-next"
        :disabled="isTransitioning || !canNavigate"
        aria-label="Další člen týmu"
        @click="next"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 57 57"
        >
          <path
            fill="currentColor"
            d="M20.401 39.377 31.28 28.5 20.4 17.599l3.349-3.349L38 28.5 23.75 42.75l-3.349-3.373z"
          />
        </svg>
      </button>
    </div>
  </div>

  <!-- Empty state -->
  <div
    v-else
    class="team-carousel-empty"
  >
    <p>Žádní členové týmu k zobrazení</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

// ============================================================================
// Types & Interfaces
// ============================================================================

interface TeamMember {
  id: number
  name: string
  position: string
  description: string
  image: string
  imageAlternate?: string | null
}

interface VisibleSlide extends TeamMember {
  offset: number
  actualIndex: number
}

interface Props {
  teamMembers?: TeamMember[]
}

// ============================================================================
// Constants
// ============================================================================

const ANIMATION_CONFIG = {
  duration: 800,
  easingPower: 3,
  swipeThreshold: 50
} as const

/**
 * SPACING_CONFIG - Controls horizontal distance between people in carousel
 *
 * HOW TO ADJUST:
 * - Increase numbers = people further apart (more space)
 * - Decrease numbers = people closer together (less space)
 *
 * POSITIONS:
 * - position1: Distance from center to first person on each side (±1)
 * - position2: ADDITIONAL distance from position1 to second person (±2)
 * - position3: ADDITIONAL distance from position2 to third person (±3)
 *
 * LEFT vs RIGHT:
 * - left: Spacing for people on LEFT side of center
 * - right: Spacing for people on RIGHT side of center
 * - Can be different for asymmetric layout
 *
 * EXAMPLE:
 * If left.position1 = 320:
 *   → Person at position -1 is 320px LEFT of center
 * If right.position1 = 550:
 *   → Person at position +1 is 550px RIGHT of center
 *
 * TOTAL DISTANCE CALCULATION:
 * - Position ±1: spacing.position1
 * - Position ±2: spacing.position1 + spacing.position2
 * - Position ±3: spacing.position1 + spacing.position2 + spacing.position3
 */
const SPACING_CONFIG = {
  left: {
    position1: 320, // Distance to first person on LEFT (-1)
    position2: 180, // Additional distance to second person on LEFT (-2)
    position3: 200 // Additional distance to third person on LEFT (-3)
  },
  right: {
    position1: 550, // Distance to first person on RIGHT (+1)
    position2: 300, // Additional distance to second person on RIGHT (+2)
    position3: 200 // Additional distance to third person on RIGHT (+3)
  }
} as const

const SCALE_CONFIG = {
  center: 1.0,
  step1: 0.3,
  step2: 0.2,
  min: 0.5
} as const

const OPACITY_CONFIG = {
  step1: 0.1,
  step2: 0.2,
  step3: 0.7
} as const

// ============================================================================
// Props & Emits
// ============================================================================

const props = withDefaults(defineProps<Props>(), {
  teamMembers: () => []
})

const emit = defineEmits<{
  'member-click': [member: TeamMember]
  'member-change': [member: TeamMember]
}>()

// ============================================================================
// Reactive State
// ============================================================================

const currentIndex = ref(0)
const isTransitioning = ref(false)
const carouselContainer = ref<HTMLElement | null>(null)
const slideOffset = ref(0)

// Touch gesture state
const touchStartX = ref(0)
const touchEndX = ref(0)

// Track which image is showing for each member (true = alternate image, false = primary image)
const showingAlternateImage = ref<Map<number, boolean>>(new Map())

// Track which content is showing for each member (true = content/description, false = lead/position)
const showingAlternateContent = ref<Map<number, boolean>>(new Map())

// ============================================================================
// Computed Properties
// ============================================================================

const slides = computed(() => props.teamMembers)

// Validation
const hasSlides = computed(() => slides.value.length > 0)
const canNavigate = computed(() => slides.value.length > 1)

// Calculate visible slides with offsets
const visibleSlides = computed((): VisibleSlide[] => {
  if (!hasSlides.value) return []

  const total = slides.value.length
  const result: VisibleSlide[] = []
  const addedIndices = new Set<number>()

  // Show more slides during transition to allow smooth fade in/out
  // When transitioning, show from -3 to +2 or -2 to +3 depending on direction
  const minOffset = slideOffset.value < 0 ? -3 : -2
  const maxOffset = slideOffset.value > 0 ? 3 : 2

  for (let offset = minOffset; offset <= maxOffset; offset++) {
    let index = currentIndex.value + offset

    // Handle wrapping with modulo
    while (index < 0) index += total
    index = index % total

    // Only add each slide once - prevent duplicates when total < visible range
    if (!addedIndices.has(index)) {
      addedIndices.add(index)
      result.push({
        ...slides.value[index],
        offset: offset + slideOffset.value,
        actualIndex: index
      })
    }
  }

  return result
})

// Get the currently centered slide for the info box
const centerSlide = computed((): TeamMember | null => {
  if (!hasSlides.value) return null
  return slides.value[currentIndex.value]
})

/**
 * Get the current image for a given member (primary or alternate)
 */
function getCurrentImage(member: TeamMember): string {
  const isShowingAlternate = showingAlternateImage.value.get(member.id) ?? false
  if (isShowingAlternate && member.imageAlternate) {
    return member.imageAlternate
  }
  return member.image
}

/**
 * Get the current text content for a given member (position/lead or description/content)
 */
function getCurrentText(member: TeamMember): string {
  const isShowingContent = showingAlternateContent.value.get(member.id) ?? false
  if (isShowingContent) {
    return member.description
  }
  return member.position
}

// ============================================================================
// Style Calculation Functions
// ============================================================================

/**
 * Calculates the style properties for a slide based on its offset from center
 * @param offset - The offset from center position (-3 to +3)
 * @returns Style object with transform, zIndex, and opacity
 */
function getSlideStyle(offset: number) {
  const absOffset = Math.abs(offset)

  // Smooth scale interpolation: center=1.0, ±1=0.7, ±2=0.5
  let scale = SCALE_CONFIG.center
  if (absOffset <= 1) {
    scale = SCALE_CONFIG.center - (absOffset * SCALE_CONFIG.step1)
  } else if (absOffset <= 2) {
    const t = absOffset - 1
    scale = (SCALE_CONFIG.center - SCALE_CONFIG.step1) - (t * SCALE_CONFIG.step2)
  } else {
    scale = SCALE_CONFIG.min
  }

  // Smooth spacing with continuous interpolation during animation
  let translateX = 0

  // Center slide offset to prevent person overlapping text
  const CENTER_OFFSET = -110

  if (offset === 0) {
    translateX = CENTER_OFFSET
  } else if (absOffset < 1) {
    // Smooth transition from center offset to position1
    const isLeft = offset < 0
    const sign = isLeft ? -1 : 1
    const spacing = isLeft ? SPACING_CONFIG.left : SPACING_CONFIG.right
    // Interpolate from CENTER_OFFSET at offset=0 to spacing.position1 at offset=±1
    translateX = CENTER_OFFSET + (sign * spacing.position1 - CENTER_OFFSET) * absOffset
  } else {
    const isLeft = offset < 0
    const sign = isLeft ? -1 : 1

    const spacing = isLeft ? SPACING_CONFIG.left : SPACING_CONFIG.right

    if (absOffset <= 1) {
      translateX = sign * spacing.position1 * absOffset
    } else if (absOffset <= 2) {
      const baseDistance = spacing.position1
      const additionalDistance = spacing.position2 * (absOffset - 1)
      translateX = sign * (baseDistance + additionalDistance)
    } else if (absOffset <= 3) {
      const baseDistance = spacing.position1 + spacing.position2
      const additionalDistance = spacing.position3 * (absOffset - 2)
      translateX = sign * (baseDistance + additionalDistance)
    } else {
      translateX = sign * (spacing.position1 + spacing.position2 + spacing.position3)
    }
  }

  // Use more granular z-index to prevent stacking during transitions
  // Multiply by 10 to get more intermediate values (e.g., offset 0.5 -> z-index 95)
  const zIndex = 100 - Math.floor(absOffset * 10)

  // Smooth opacity interpolation with fade out at edges
  let opacity = 1.0
  if (absOffset <= 1) {
    opacity = 1.0 - (absOffset * OPACITY_CONFIG.step1)
  } else if (absOffset <= 2) {
    const t = absOffset - 1
    opacity = (1.0 - OPACITY_CONFIG.step1) - (t * OPACITY_CONFIG.step2)
  } else if (absOffset <= 3) {
    const t = absOffset - 2
    const opacityAt2 = 1.0 - OPACITY_CONFIG.step1 - OPACITY_CONFIG.step2
    opacity = opacityAt2 - (t * OPACITY_CONFIG.step3)
  } else {
    opacity = 0
  }

  return {
    transform: `translateX(${translateX}px) scale(${scale})`,
    zIndex,
    opacity
  }
}

// ============================================================================
// Navigation Functions
// ============================================================================

/**
 * Navigates to the next slide with smooth animation
 */
function next(): void {
  if (!canNavigate.value || isTransitioning.value) return

  isTransitioning.value = true

  let start: number | undefined
  const duration = ANIMATION_CONFIG.duration

  const animate = (timestamp: number) => {
    if (start === undefined) start = timestamp
    const elapsed = timestamp - start
    const progress = Math.min(elapsed / duration, 1)

    const eased = 1 - Math.pow(1 - progress, ANIMATION_CONFIG.easingPower)
    slideOffset.value = -eased

    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      slideOffset.value = 0
      currentIndex.value = (currentIndex.value + 1) % slides.value.length
      isTransitioning.value = false
      emit('member-change', slides.value[currentIndex.value])
    }
  }

  requestAnimationFrame(animate)
}

/**
 * Navigates to the previous slide with smooth animation
 */
function prev(): void {
  if (!canNavigate.value || isTransitioning.value) return

  isTransitioning.value = true

  let start: number | undefined
  const duration = ANIMATION_CONFIG.duration

  const animate = (timestamp: number) => {
    if (start === undefined) start = timestamp
    const elapsed = timestamp - start
    const progress = Math.min(elapsed / duration, 1)

    const eased = 1 - Math.pow(1 - progress, ANIMATION_CONFIG.easingPower)
    slideOffset.value = eased

    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      slideOffset.value = 0
      const total = slides.value.length
      currentIndex.value = (currentIndex.value - 1 + total) % total
      isTransitioning.value = false
      emit('member-change', slides.value[currentIndex.value])
    }
  }

  requestAnimationFrame(animate)
}

// ============================================================================
// Touch & Keyboard Handlers
// ============================================================================

/**
 * Handles touch start event for swipe detection
 */
function handleTouchStart(e: TouchEvent): void {
  const touch = e.touches[0]
  if (!touch) return
  touchStartX.value = touch.clientX
}

/**
 * Handles touch move event for swipe detection
 */
function handleTouchMove(e: TouchEvent): void {
  const touch = e.touches[0]
  if (!touch) return
  touchEndX.value = touch.clientX
}

/**
 * Handles touch end event and triggers navigation if swipe threshold met
 */
function handleTouchEnd(): void {
  const diff = touchStartX.value - touchEndX.value

  if (Math.abs(diff) > ANIMATION_CONFIG.swipeThreshold) {
    if (diff > 0) {
      next()
    } else {
      prev()
    }
  }

  touchStartX.value = 0
  touchEndX.value = 0
}

/**
 * Handles keyboard navigation with arrow keys
 */
function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'ArrowLeft') {
    e.preventDefault()
    prev()
  } else if (e.key === 'ArrowRight') {
    e.preventDefault()
    next()
  }
}

/**
 * Handles click on member info button - toggles between primary and alternate image/content
 */
function handleButtonClick(slide: TeamMember): void {
  if (!slide.imageAlternate) {
    // No alternate image available, just emit the click event
    emit('member-click', slide)
    return
  }

  // Toggle both image and content for this member
  const currentImageState = showingAlternateImage.value.get(slide.id) ?? false
  const currentContentState = showingAlternateContent.value.get(slide.id) ?? false

  showingAlternateImage.value.set(slide.id, !currentImageState)
  showingAlternateContent.value.set(slide.id, !currentContentState)

  emit('member-click', slide)
}

// ============================================================================
// Lifecycle Hooks
// ============================================================================

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})
</script>
